import { noop } from 'lodash';
import type { CollectionInfoFragment } from '@frameio/graphql-client/generated';
import {
  GetCollectionsForProject,
  useDuplicateCollectionMutation,
} from '@frameio/graphql-client/generated';
import { useLoadingToast } from '@frameio/shared/resources';
import { useTrack } from '@frameio/shared/segment/hooks';
import TEXT from './generated/TEXT/useDuplicateCollection';

type DuplicateCollectionOpts = {
  projectId: string;
  onSuccess?: (result: CollectionInfoFragment) => void;
};

const DEFAULT_OPTS: Partial<DuplicateCollectionOpts> = {
  onSuccess: noop,
};

type DuplicateCollectionReturn = {
  duplicateCollection: (
    collectionId: string,
    name: string,
    isPrivate: boolean
  ) => void;
};

/**
 * The useDuplicateCollection hook can be used to duplicate a collection in a project.
 * @param opts.projectId the id of the project to duplicate a collection in
 * @param opts.onSuccess a function to call when the collection is successfully duplicated
 * @returns a function to call to duplicate a collection given an collectionId and a name
 */
export function useDuplicateCollection(
  opts?: DuplicateCollectionOpts
): DuplicateCollectionReturn {
  const { projectId, onSuccess } = {
    ...DEFAULT_OPTS,
    ...opts,
  } as DuplicateCollectionOpts;

  const { addToast, updateAndHideToast } = useLoadingToast();

  const [duplicateCollectionMutation] = useDuplicateCollectionMutation();
  const track = useTrack();

  const duplicateCollection = (
    collectionId: string,
    name: string,
    isPrivate: boolean
  ) => {
    track(
      'collection-duplicated',
      { collection_id: collectionId },
      { logToDatadog: true }
    );

    const toastId = addToast(TEXT.duplicating);

    const handleSuccess = (duplicatedCollection: CollectionInfoFragment) => {
      onSuccess?.(duplicatedCollection);

      updateAndHideToast(toastId, {
        title: TEXT.success,
        variant: 'success',
      });
    };

    const handleFailure = () => {
      updateAndHideToast(toastId, { title: TEXT.error, variant: 'error' });
    };

    return duplicateCollectionMutation({
      refetchQueries: [
        {
          query: GetCollectionsForProject,
          variables: { projectId },
        },
        'GetAllCollections',
      ],
      variables: {
        input: {
          collectionId,
          name: TEXT.collectionCopyName(name) as string,
          private: isPrivate,
        },
      },
      onCompleted: (data) => {
        const duplicatedCollection = data?.duplicateCollection
          ?.collection as CollectionInfoFragment;

        if (!duplicatedCollection) {
          handleFailure();
          return;
        }

        handleSuccess(duplicatedCollection);
      },
      onError: () => {
        handleFailure();
      },
    });
  };

  return {
    duplicateCollection,
  };
}

TEXT.DEFINE({
  /** Default name for duplicated collections */
  collectionCopyName: (name: CollectionInfoFragment['name']) => `${name} copy`,
  /** Loading toast title while duplication is in progress */
  duplicating: 'Duplicating collection',
  /** Toast title shown after a successful duplication */
  success: 'Collection duplicated',
  /** Toast title shown when duplication fails */
  error: 'Collection could not be duplicated',
});
